import React from 'react'
import styled, { css } from 'styled-components'

import { Array, pipe, Set } from '@blade/std-lib'
import { WalletModelSelectItem } from '@root/entities/wallet/ui/desktop/wallet-model-select-item'
import { useTranslation } from '@root/shared/localization'
import { FadeOverflow, InfoMessage, WalletIcon } from '@root/shared/ui-kit'

import type { CurrencyModel } from '@root/entities/currency'
import type { WalletId } from '@root/entities/wallet'
import type { WalletModel } from '@root/entities/wallet/atoms'

export type WalletsListSelectionProps =
  | {
      multiple?: false | undefined
      selectedWalletId: WalletId | undefined
      onWalletSelected: (wallet: WalletModel) => void
    }
  | {
      multiple: true
      selectedWalletIds: Array<WalletId>
      onWalletsSelected: (wallets: Array<WalletModel>) => void
    }

export type WalletsListItemsProps = {
  wallets: Array<WalletModel>
  currency?: CurrencyModel
} & WalletsListSelectionProps

export const WalletsListItems: React.FC<WalletsListItemsProps> = ({
  wallets,
  currency,
  ...selectionProps
}) => {
  const { t } = useTranslation()

  if (wallets.length === 0) {
    return (
      <InfoMessage
        intent="empty"
        variant="dt"
        icon={<WalletIcon />}
        title={t('wallet.search-wallet')}
        description={t('common.no-items-found')}
        retryText={t('common.clear')}
        css={css`
          height: 100%;
        `}
      />
    )
  }

  const handleWalletSelected = (wallet: WalletModel) => {
    if (selectionProps.multiple) {
      const currentSelection = Set.create(selectionProps.selectedWalletIds)
      const newSelection = [...Set.toggle(wallet.id)(currentSelection)]

      const selectedWallets = pipe(
        newSelection,
        Array.map((id) => wallets.find((w) => w.id === id)),
        Array.filter((w) => w !== undefined),
      )

      selectionProps.onWalletsSelected(selectedWallets)
      return
    }

    selectionProps.onWalletSelected(wallet)
  }

  const renderWalletItem = (wallet: WalletModel) => {
    const isSelected = selectionProps.multiple
      ? selectionProps.selectedWalletIds.includes(wallet.id)
      : wallet.id === selectionProps.selectedWalletId

    return (
      <WalletItem
        key={wallet.id}
        wallet={wallet}
        active={isSelected}
        onClick={() => handleWalletSelected(wallet)}
        currency={currency}
        selectionMode={selectionProps.multiple ? 'check' : 'radio'}
      />
    )
  }

  return (
    <Root mode="always" variant="vertical" blockStart={6} blockEnd={24}>
      {wallets.map(renderWalletItem)}
    </Root>
  )
}

const Root = styled(FadeOverflow)`
  max-height: 100%;
  overflow-y: auto;
  align-content: start;

  display: grid;
  gap: 6px;

  padding-top: 4px;
  padding-bottom: 14px;
  padding-inline: 10px;
`

const WalletItem = styled(WalletModelSelectItem)`
  background: var(--backgroundA-2);
  border-radius: 8px;

  &[aria-selected='true'] {
    background: var(--backgroundA-3);
  }
  &:hover {
    background: var(--backgroundA-3);
  }
`
